import React, { useMemo } from 'react';
import classes from './SelectConnection.module.scss';
import Typography from 'new/elements/Typography';
import useSelectConnection from 'new/elements/flows/SelectConnection/useSelectConnection';
import { EmptyState } from 'new/elements/EmptyState';
import { getCredentialCount } from 'js/reducers/credentials';
import { useSelector } from 'react-redux';
import cx from 'classnames';

const Connection = ({ connection, metas, credentialCount }) => {
  const { selectedConnection, onConnectionSelected, connectionsComingSoon } = useSelectConnection();
  const isChecked = selectedConnection === connection;
  const meta = metas[connection];
  const soon = connectionsComingSoon.includes(connection);

  const onChange = (e) => {
    if (soon) return;
    const { value } = e.target;
    onConnectionSelected?.(value);
  };

  return (
    <label
      className={cx(classes.rootConnectionContainer, {
        [classes.rootConnectionContainer_checked]: isChecked,
        [classes.rootConnectionContainer_disabled]: (!isChecked && selectedConnection) || soon,
        [classes.rootConnectionContainer_soon]: soon,
      })}
    >
      <input
        name="radio-option"
        type="radio"
        value={connection}
        checked={isChecked}
        style={{ display: 'none' }}
        disabled={false}
        onChange={onChange}
      />
      <div data-testid={`connection-card-${meta.name}`} className={classes.connection}>
        <div
          className={classes.img_placeholder}
          style={meta.iconSm ? { backgroundImage: `url(${meta.iconSm})` } : {}}
        />
        <h3 className={classes.connector_title}>{meta.name}</h3>
      </div>
      {soon ? (
        <div className={classes.comingSoonBadge}>
          <span>Coming Soon</span>
        </div>
      ) : credentialCount > 0 ? (
        <div
          className={classes.credentialCountBadge}
          role="img"
          aria-label={`${credentialCount} ${credentialCount === 1 ? 'credential' : 'credentials'}`}
          title={`${credentialCount} ${credentialCount === 1 ? 'credential' : 'credentials'}`}
        >
          <span>{credentialCount}</span>
        </div>
      ) : null}
    </label>
  );
};

export const Connections = ({ showOnlyWithCredentials = false }) => {
  const { connections = [], isLoadingVendors } = useSelectConnection();
  const byType = useSelector(
    (state: { credentials?: { byType?: Record<string, number[]> } }) => state.credentials?.byType,
  );
  const metas = useSelector(
    (state: { paging: { metaInfoById: Record<string, unknown> } }) => state.paging.metaInfoById,
  );

  const visibleConnections = useMemo(
    () =>
      showOnlyWithCredentials
        ? connections.filter((c) => getCredentialCount(byType, c) > 0)
        : connections,
    [showOnlyWithCredentials, connections, byType],
  );

  return (
    <section className={classes.connectionsContainer}>
      <Typography variant="overline">Select a connector type</Typography>
      {!isLoadingVendors && visibleConnections.length === 0 ? (
        <EmptyState title="No connectors match your filters." />
      ) : (
        <div className={classes.connections}>
          {visibleConnections.map((connection) => (
            <Connection
              key={connection}
              metas={metas}
              connection={connection}
              credentialCount={getCredentialCount(byType, connection)}
            />
          ))}
        </div>
      )}
    </section>
  );
};
